import React from 'react';
import * as THREE from 'three';
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import { GetRoundNum } from "./Common";
import ReactDOMServer from 'react-dom/server';
import { ImArrowDown, ImArrowUp } from "react-icons/im";
import { useViewerStore } from "../../../../../store/store";
import { TreeGroup } from '../../../../../@types/types';

export const TREE_IMPACT_GREEN_THRESHOLD = 0.05;

export function isGreenImpactTree(treeImpact: number): boolean {
    return treeImpact > TREE_IMPACT_GREEN_THRESHOLD;
}

/** True when `GhostOnlyGreenImpactTrees` capability is active (see `featureFlags.ts` for dev override). */
export function isGhostOnlyGreenImpactTreesEnabled(): boolean {
    return useViewerStore.getState().ghostOnlyGreenImpactTrees === true;
}

export function shouldShowTreeMultiplierLabel(treeImpact: number): boolean {
    if (isGhostOnlyGreenImpactTreesEnabled()) {
        return isGreenImpactTree(treeImpact);
    }
    return treeImpact > 0;
}

type TreeImpactUserData = {
    activeTreeImpact?: number;
    impactAtGhost?: number;
    hasReevaluatedSinceGhost?: boolean;
};

function normalizeTreeImpact(value: unknown): number | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

export function getReevaluatedActiveImpact(tree: {
    treeImpact?: number;
    delStatus?: boolean;
    userData?: TreeImpactUserData;
}): number {
    if (!isGhostOnlyGreenImpactTreesEnabled() || !tree.delStatus) {
        return tree.treeImpact ?? 0;
    }

    const { impactAtGhost, activeTreeImpact, hasReevaluatedSinceGhost } = tree.userData ?? {};
    const liveImpact = tree.treeImpact ?? 0;
    const normalizedActiveImpact = normalizeTreeImpact(activeTreeImpact);
    const normalizedImpactAtGhost = normalizeTreeImpact(impactAtGhost);

    if (!hasReevaluatedSinceGhost && normalizedImpactAtGhost !== undefined) {
        return normalizedImpactAtGhost;
    }

    // Never fall back to ghosted liveImpact here — it is near zero/negative while delStatus is true.
    if (hasReevaluatedSinceGhost && normalizedActiveImpact !== undefined) {
        return normalizedActiveImpact;
    }

    if (normalizedImpactAtGhost !== undefined) {
        return normalizedImpactAtGhost;
    }

    return liveImpact;
}

/** Impact used for ghost eligibility checks and auto-unghost decisions. */
export function getTreeImpactForEligibility(tree: {
    treeImpact?: number;
    delStatus?: boolean;
    userData?: TreeImpactUserData;
}): number | undefined {
    if (!isGhostOnlyGreenImpactTreesEnabled() || !tree.delStatus) {
        return normalizeTreeImpact(tree.treeImpact) ?? 0;
    }

    const { impactAtGhost, activeTreeImpact, hasReevaluatedSinceGhost } = tree.userData ?? {};
    const normalizedActiveImpact = normalizeTreeImpact(activeTreeImpact);
    const normalizedImpactAtGhost = normalizeTreeImpact(impactAtGhost);

    if (hasReevaluatedSinceGhost) {
        return normalizedActiveImpact;
    }

    if (normalizedImpactAtGhost !== undefined) {
        return normalizedImpactAtGhost;
    }

    return normalizeTreeImpact(tree.treeImpact);
}

export function hasCompletedGhostImpactReevaluation(tree: {
    userData?: TreeImpactUserData;
}): boolean {
    if (!tree.userData?.hasReevaluatedSinceGhost) return false;
    return normalizeTreeImpact(tree.userData.activeTreeImpact) !== undefined;
}

export function canGhostTreeSelection(trees: { treeImpact?: number; delStatus?: boolean; userData?: { activeTreeImpact?: number } }[]): boolean {
    if (!isGhostOnlyGreenImpactTreesEnabled()) return true;
    if (!trees?.length) return false;
    if (trees.every(t => t.delStatus === true)) return true;
    return trees.every(t => {
        const impact = getTreeImpactForEligibility(t);
        return impact !== undefined && isGreenImpactTree(impact);
    });
}

export function UpdateTreeMuliplier(modelGroup: TreeGroup) {
    const treeLabelGroup = modelGroup.children.find((group: { name: string }) => group.name === 'treeLabelGroup');
    const isTreeMultiplierLabelVisible = useViewerStore.getState().isTreeMultiplierLabelVisible;

    // Clear all existing children from the treeLabelGroup
    while (treeLabelGroup && treeLabelGroup.children.length > 0) {
        treeLabelGroup.remove(treeLabelGroup.children[0]);
    }

    const treeArr: any[] = [];

    modelGroup.traverse((child: any) => {
        if (child.meshType === 'tree' && child.name === 'wrapper') {
            treeArr.push(child.parent);
        }
    })

    treeArr.forEach(tree => {
        const { position, treeImpact, userData, totalH, delStatus } = tree;
        const impactForDisplay =
            delStatus && isGhostOnlyGreenImpactTreesEnabled()
                ? getReevaluatedActiveImpact(tree)
                : (treeImpact ?? 0);
        const tree_multiplier = GetRoundNum(impactForDisplay, 4);
        const labelColor = getLabelCol(tree_multiplier);
        const treeLabelHeight = totalH + 0.5;
        const content = `${getMultiplierLabelPrefix(true, false, 12)}${tree_multiplier}`;
        const treeLabelObj = createLabel(content, labelColor, new THREE.Vector3(position.x, treeLabelHeight, position.z));
        treeLabelObj.name = Number(tree_multiplier);
        treeLabelObj.userData.treeId = userData.treeId
        treeLabelObj.visible = isTreeMultiplierLabelVisible;
        userData.multiplier = {};
        userData.multiplier.value = tree_multiplier || '0';
        userData.multiplier.color = labelColor;

        if (!treeLabelGroup) return;
        const existingChildIndex = treeLabelGroup.children.findIndex((child: any) => child.userData.treeId === userData.treeId);
        if (existingChildIndex !== -1) {
            const existing = treeLabelGroup.children[existingChildIndex];
            if (shouldShowTreeMultiplierLabel(impactForDisplay)) {
                treeLabelGroup.remove(existing);
                treeLabelGroup.add(treeLabelObj);
            } else {
                treeLabelGroup.remove(existing);
            }
        } else {
            if (shouldShowTreeMultiplierLabel(impactForDisplay)) treeLabelGroup.add(treeLabelObj);
        }
    });
}

export function getMultiplierLabelPrefix(upArrow: boolean, arrowColor: boolean, size: number) {
    const color = arrowColor ? (upArrow ? '#0f0' : '#f00') : undefined;
    const arrowElement = React.createElement(
        upArrow ? ImArrowUp : ImArrowDown,
        { size, color }
    );
    const arrowUpHTML = ReactDOMServer.renderToString(arrowElement);
    return `M ${arrowUpHTML}`;
}

export function createLabel(contents: string, color: string, position: THREE.Vector3) {
    const div = document.createElement('div');
    div.className = 'tree-epc-label';
    div.style.color = color;
    div.innerHTML = contents;

    const label: any = new CSS2DObject(div);
    label.labelColor = color;
    label.position.copy(position);
    return label;
}

function getLabelCol(value: number) {
    var r = 0, g = 0, b = 0;
    if (value <= 0) {
        r = 255;
    } else if (value > 0 && value <= 0.05) {
        var ratio = value / 0.05
        g = Math.round(100 + 155 * ratio);
        r = Math.round(255 * (1 - ratio));
    } else if (value > 0.05) {
        g = 255;
    }
    const h = r * 0x10000 + g * 0x100 + b * 0x1;
    const hexStr = '#' + ('000000' + h.toString(16)).slice(-6)
    return hexStr;
}
